import React, { useState, useRef, useContext } from 'react';
import styled from '@emotion/styled';
import { useLayoutEffect, getWindowWidth } from '@tal-gel/core';
import { withTheme } from '@tal-gel/theming';

//import { ScreenDetectorContext } from '../grid';

import {
    GelIcon as Icon,
    GelScreenDetectorContext as ScreenDetectorContext} from '@tal-gel/components';

const selfAdaptivePositionDefaults = {
  top: false,
  bottom: false,
  left: false,
  right: false,
  topLeft: false,
  topRight: false,
  bottomLeft: false,
  bottomRight: false
};

const TooltipComponent = React.forwardRef(({ 
  theme,
  selfAdaptive, // automatically detects the best position to display tooltip
  top, // tip box to be positioned at the top of icon
  bottom, // tip box to be positioned at the bottom of icon
  left, // tip box to be positioned at the left of icon
  right, // tip box to be positioned at the right of icon
  topLeft, // tip box to be positioned at the top left of icon
  topRight, // tip box to be positioned at the top right of icon
  bottomLeft, // tip box to be positioned at the bottom left of icon
  bottomRight, // tip box to be positioned at the bottom right of icon
  icon, // icon name to override the theme
  size, // icon size to override the theme
  color, // icon color to override the theme,
  fixedAtTopLevel, // fixes the tip box at the top level (to break any layering restrictions)
  dark = true, // whether dark backgrond or not
  light = false, // whether light backgrond or not
  maxWidth, // max width of the tip box
  inline, // makes the tooltip icon inline
  children, // content of the tip box
  ...props
}, forwardedRef) => {
  const iconRef = useRef();
  const getIconRef = () => forwardedRef || iconRef;

  const { isXsScreen, screen } = useContext(ScreenDetectorContext)
  
  const [selfAdaptivePosition, setSelfAdaptivePosition] = useState({
    ...selfAdaptivePositionDefaults,
    right: true // Try `right` first
  });

  if (selfAdaptive) {
    top = selfAdaptivePosition.top;
    bottom = selfAdaptivePosition.bottom;
    left = selfAdaptivePosition.left;
    right = selfAdaptivePosition.right;
    topLeft = selfAdaptivePosition.topLeft;
    topRight = selfAdaptivePosition.topRight;
    bottomLeft = selfAdaptivePosition.bottomLeft;
    bottomRight = selfAdaptivePosition.bottomRight;
  } else {
    const numOfPositions = getNumOfPositions({ // check how many positions are specified
      top, bottom, left, right, topLeft, topRight, bottomLeft, bottomRight
    });
    if (numOfPositions === 0) { // throw error if no position is specified
      throw 'Specify a position: top, bottom, left, right, topLeft, topRight, bottomLeft, bottomRight';
    }
    if (numOfPositions > 1) { // throw error if more than one position are specified
      throw 'Only one position can be specified';
    }
  }
  
  if (dark && light) {
    dark = false;
  }
  if (!dark && !light) {
    light = true;
  }

  const [showTipBox, setShowTipBox] = useState(false);
  const tipBoxRef = useRef();
  const [tipBoxDimension, setTipBoxDimension] = useState();
  
  const arrowDownRef = useRef();
  const arrowUpRef = useRef();
  const arrowLeftRef = useRef();
  const arrowRightRef = useRef();
  const [tipBoxFixedPosition, setTipBoxFixedPosition] = useState();
  const [arrowDownFixedPosition, setArrowDownFixedPosition] = useState();
  const [arrowUpFixedPosition, setArrowUpFixedPosition] = useState();
  const [arrowLeftFixedPosition, setArrowLeftFixedPosition] = useState();
  const [arrowRightFixedPosition, setArrowRightFixedPosition] = useState();

  const tipBoxDimensionCompensated = useRef(false);

  useLayoutEffect(() => { // after render but before browser repaints (makes it visible to users)
    if (showTipBox && tipBoxRef.current) { // if the tip box is shown
      // Store width and height of the current tip box, 
      // as the dimension will determine precise positioning of the tip box
      // We want to position the tip box correctly before browser repaints
      setTipBoxDimension({ // triggers synchronous re-render (before browser repaints)
        width: tipBoxRef.current.offsetWidth ? (
          tipBoxDimensionCompensated.current ? tipBoxRef.current.offsetWidth : tipBoxRef.current.offsetWidth + 1
        ) : 100,
        height: tipBoxRef.current.offsetHeight ? (
          tipBoxDimensionCompensated.current ? tipBoxRef.current.offsetHeight : tipBoxRef.current.offsetHeight + 1
        ) : 50
      });
      tipBoxDimensionCompensated.current = true;
      // Note that `|| 100` and `|| 50` are really just a hack to make tests work, 
      // as Jest does not render DOM so `offsetWidth` and `offsetHeight` are always 0 for the tests
      // As the only case where `offsetWidth` or `offsetHeight` is 0 is when the code runs in tests, 
      // we can safely put `|| 100` and `|| 50` as a hack to assign values 
      // to `offsetWidth` and `offsetHeight` while not affecting production
    }
  }, [showTipBox]); // only invoked if `showTipBox` changes

  useLayoutEffect(() => {
    // If fixedAtTopLevel to break layering restrictions, we want to use the current positions 
    // of the tip box and arrow in the window, to set their fixed positions when `position: fixed`
    // So effectively the tipbox and the arrow appear to be still at the same spot, but fixed at
    // the top level to avoid getting blocked by other layers
    if (fixedAtTopLevel && showTipBox && tipBoxRef.current && tipBoxDimension && !tipBoxFixedPosition) {
      const rect = tipBoxRef.current.getBoundingClientRect();
      setTipBoxFixedPosition({
        x: rect.x,
        y: rect.y
      });
      if (arrowDownRef.current) {
        const rect = arrowDownRef.current.getBoundingClientRect();
        setArrowDownFixedPosition({
          x: rect.x,
          y: rect.y
        });
      }
      if (arrowUpRef.current) {
        const rect = arrowUpRef.current.getBoundingClientRect();
        setArrowUpFixedPosition({
          x: rect.x,
          y: rect.y
        });
      }
      if (arrowLeftRef.current) {
        const rect = arrowLeftRef.current.getBoundingClientRect();
        setArrowLeftFixedPosition({
          x: rect.x,
          y: rect.y
        });
      }
      if (arrowRightRef.current) {
        const rect = arrowRightRef.current.getBoundingClientRect();
        setArrowRightFixedPosition({
          x: rect.x,
          y: rect.y
        });
      }
    }
  });

  useLayoutEffect(() => {
    if (selfAdaptive && showTipBox && getIconRef().current && tipBoxDimension) {
      const iconRect = getIconRef().current.getBoundingClientRect();
      const centerX = (iconRect.right + iconRect.left) / 2;
      const centerY = (iconRect.bottom + iconRect.top) / 2;
      const arrowThickness = convertToNumberFrom(theme.Tooltip.Arrow.thickness);
      const gutterAroundIcon = convertToNumberFrom(theme.Tooltip.gutterAroundIcon);
      const offsetForCornerTipBox = convertToNumberFrom(theme.Tooltip.offsetForCornerTipBox);
      const distanceFromWindowBoundaries = 15;

      // See comment in use-window-size for window.outerWidth and window.innerHeight
      const fitsFullRight = () => (
        iconRect.right + gutterAroundIcon + arrowThickness + tipBoxDimension.width + 
        distanceFromWindowBoundaries < getWindowWidth()
      );
      const fitsFullLeft = () => (
        iconRect.left - gutterAroundIcon - arrowThickness - tipBoxDimension.width - 
        distanceFromWindowBoundaries > 0
      );
      const fitsFullTop = () => (
        iconRect.top - gutterAroundIcon - arrowThickness - 
        tipBoxDimension.height - distanceFromWindowBoundaries > 0
      );
      const fitsFullBottom = () => (
        iconRect.bottom + gutterAroundIcon + arrowThickness + 
        tipBoxDimension.height + distanceFromWindowBoundaries < window.innerHeight
      );
      const fitsHalfTop = () => (
        centerY - tipBoxDimension.height / 2 - distanceFromWindowBoundaries > 0
      );
      const fitsHalfBottom = () => (
        centerY + tipBoxDimension.height / 2 + distanceFromWindowBoundaries < window.innerHeight
      );
      const fitsHalfRight = () => (
        centerX + tipBoxDimension.width / 2 + distanceFromWindowBoundaries < getWindowWidth()
      );
      const fitsHalfLeft = () => (
        centerX - tipBoxDimension.width / 2 - distanceFromWindowBoundaries > 0
      );

      let pos;
      if (fitsFullRight() && fitsHalfTop() && fitsHalfBottom()) { // right
        pos = 'right';
      } else if (fitsFullTop() && fitsFullRight()) { // top right
        pos = 'topRight';
      } else if (fitsFullTop() && fitsHalfLeft() && fitsHalfRight()) {// top
        pos = 'top';
      } else if (fitsFullTop() && fitsFullLeft()) { // top left
        pos = 'topLeft';
      } else if (fitsFullLeft() && fitsHalfTop() && fitsHalfBottom()) { // left
        pos = 'left';
      } else if (fitsFullBottom() && fitsFullLeft()) { // bottom left
        pos = 'bottomLeft';
      } else if (fitsFullBottom() && fitsHalfLeft() && fitsHalfRight()) { // bottom
        pos = 'bottom';
      } else if (fitsFullBottom() && fitsFullRight()) { // bottom right
        pos = 'bottomRight';
      } else { // TODO: Screen too small; doesn't fit anywhere...
        setShowTipBox(false);
      }
      if (pos) {
        setSelfAdaptivePosition(prevState => {
          // Only updates if prev position wasn't the position determined
          if (prevState[pos]) return prevState;
          setTipBoxFixedPosition();
          return {
            ...selfAdaptivePositionDefaults,
            [pos]: true
          };
        });
      }
    }
  });

  const onMouseEnter = () => {
    setShowTipBox(true); // show tip box on hover
  };

  const onMouseLeave = () => {
    setShowTipBox(false); // hide tip box when not on hover
    if (fixedAtTopLevel) {
      // Remove fixed positions
      setTipBoxFixedPosition();
      setArrowDownFixedPosition();
      setArrowUpFixedPosition();
      setArrowLeftFixedPosition();
      setArrowRightFixedPosition();
    }
  };

  return (
    <TooltipContainer inline={inline} {...props}>
      <Icon 
        data-cy="gel-tooltip-icon"
        ref={getIconRef()}
        inline={inline}
        noInlineGap
        {...theme.Tooltip.Icon} 
        {...icon && { name: icon }} // if icon name is specified, use it to override the theme
        {...size && { width: size }} // if icon size is specified, use it to override the theme
        {...color && { color }} // if icon color is specified, use it to override the theme
        onMouseEnter={onMouseEnter} 
        onMouseLeave={onMouseLeave} 
        onFocus={onMouseEnter} // tabbed in by keyboard
        onBlur={onMouseLeave}  // tabbed out by keyboard
        onClick={() => {}} // makes the icon focusable, but does nothing upon click
        ariaLabel={children}
        role="tooltip"
      />
      { showTipBox &&
        <TipBox
          data-cy="gel-tooltip-tip-box"
          theme={theme}
          ref={tipBoxRef}
          maxWidth={maxWidth}
          dimension={tipBoxDimension} // determines precise positioning
          fixedPosition={tipBoxFixedPosition}
          fixedAtTopLevel={fixedAtTopLevel}
          iconWidth={size} // icon width that overrides the theme
          top={top}
          bottom={bottom}
          left={left}
          right={right}
          topLeft={topLeft}
          topRight={topRight}
          bottomLeft={bottomLeft}
          bottomRight={bottomRight}
          dark={dark} 
          light={light}
          isXsScreen={isXsScreen(screen)}
        >
          { children }
        </TipBox>
      }
      {/* Arrow connected to the tip box */}
      { showTipBox && (top || topLeft || topRight) && 
        <ArrowDown 
          data-cy="gel-tooltip-arrow-down"
          theme={theme} 
          iconWidth={size} 
          ref={arrowDownRef} 
          fixedPosition={arrowDownFixedPosition} 
          fixedAtTopLevel={fixedAtTopLevel}
          dark={dark} 
          light={light}
        /> 
      }
      { showTipBox && (bottom || bottomLeft || bottomRight) && 
        <ArrowUp 
          data-cy="gel-tooltip-arrow-up"
          theme={theme} 
          iconWidth={size} 
          ref={arrowUpRef} 
          fixedPosition={arrowUpFixedPosition} 
          fixedAtTopLevel={fixedAtTopLevel}
          dark={dark} 
          light={light}
        /> 
      }
      { showTipBox && left && 
        <ArrowRight 
          data-cy="gel-tooltip-arrow-right"
          theme={theme} 
          iconWidth={size} 
          ref={arrowRightRef} 
          fixedPosition={arrowRightFixedPosition} 
          fixedAtTopLevel={fixedAtTopLevel}
          dark={dark} 
          light={light}
        /> 
      }
      { showTipBox && right && 
        <ArrowLeft 
          data-cy="gel-tooltip-arrow-left"
          theme={theme} 
          iconWidth={size} 
          ref={arrowLeftRef} 
          fixedPosition={arrowLeftFixedPosition} 
          fixedAtTopLevel={fixedAtTopLevel}
          dark={dark} 
          light={light}
        /> 
      }
    </TooltipContainer>
  );
});

const TooltipContainer = styled.div({ // container that wraps everything
  position: 'relative',
}, ({ inline }) => ({
  display: !inline ? 'flex' : 'inline-flex',
  ...inline && {
    verticalAlign: 'middle'
  }
}));

const TipBox = styled.div( // the tip box to show and hide
  {
    position: 'fixed', // for measuring the supposed dimension without restrictions, prior to browser paint
    left: 0,
    top: 0,
    zIndex: 10,
  },
  ({ theme, dimension, maxWidth, iconWidth,
    top, bottom, left, right, dark, light,
    topLeft, topRight, bottomLeft, bottomRight,
    fixedPosition, fixedAtTopLevel, isXsScreen
  }) => {
    const arrowThickness = convertToNumberFrom(theme.Tooltip.Arrow.thickness);
    const gutterAroundIcon = convertToNumberFrom(theme.Tooltip.gutterAroundIcon);
    const offsetForCornerTipBox = convertToNumberFrom(theme.Tooltip.offsetForCornerTipBox);

    return {
      ...theme.Tooltip.Tipbox({ isXsScreen, dark: dark || !light }),
      ...maxWidth && { maxWidth }, // user specified max width
      // Set the supposed width of tip box, prior to browser paint. 
      // If width unspecified and when position: 'fixed', the tip box will be made accommodate full text on signle line
      // This step is for measuring the supposed dimension of tip box in useLayoutEffect
      //...!dimension && { width },

      // After useLayoutEffect, set explicit width to the measured supposed dimension
      // so it can break restrictions when position: 'absolute'
      ...dimension && { position: 'absolute', width: dimension.width },

      // If fixedAtTopLevel is true but fixedPosition is unavailable yet, 
      // render it normally/unfixed first and then set fixedPosition in useLayoutEffect 
      // (browser won't paint till useLayoutEffect is done,
      // and till all latest states have been reflected in the render)
      ...!fixedAtTopLevel || !fixedPosition ? { 
        ...top && dimension && { // determine precise positioning for 'top'
          top: -(dimension.height + 2 * arrowThickness + gutterAroundIcon - 1), 
          left: -(dimension.width / 2 - (iconWidth || theme.Tooltip.Icon.width) / 2) 
        },
        ...bottom && dimension && { // determine precise positioning for 'bottom'
          top: (iconWidth || theme.Tooltip.Icon.width) + 2 * arrowThickness + gutterAroundIcon, 
          left: -(dimension.width / 2 - (iconWidth || theme.Tooltip.Icon.width) / 2) 
        },
        ...left && dimension && {  // determine precise positioning for 'left'
          top: -(dimension.height / 2 - (iconWidth || theme.Tooltip.Icon.width) / 2), 
          left: -(dimension.width + 2 * arrowThickness + gutterAroundIcon) 
        },
        ...right && dimension && { // determine precise positioning for 'right'
          top: -(dimension.height / 2 - (iconWidth || theme.Tooltip.Icon.width) / 2), 
          left: (iconWidth || theme.Tooltip.Icon.width) + 3 * arrowThickness + gutterAroundIcon 
        },
        ...topLeft && dimension && { // determine precise positioning for 'top left'
          top: -(dimension.height + 2 * arrowThickness + gutterAroundIcon - 1), 
          left: -(dimension.width - (iconWidth || theme.Tooltip.Icon.width)) + offsetForCornerTipBox
        },
        ...topRight && dimension && { // determine precise positioning for 'top right'
          top: -(dimension.height + 2 * arrowThickness + gutterAroundIcon - 1), 
          left: -offsetForCornerTipBox 
        },
        ...bottomLeft && dimension && { // determine precise positioning for 'bottom left'
          top: (iconWidth || theme.Tooltip.Icon.width) + 2 * arrowThickness + gutterAroundIcon, 
          left: -(dimension.width - (iconWidth || theme.Tooltip.Icon.width)) + offsetForCornerTipBox
        },
        ...bottomRight && dimension && { // determine precise positioning for 'bottom right'
          top: (iconWidth || theme.Tooltip.Icon.width) + 2 * arrowThickness + gutterAroundIcon, 
          left: -offsetForCornerTipBox
        },
      } : { // When fixedPosition is available
        position: 'fixed',
        left: fixedPosition.x,
        top: fixedPosition.y
      }      
    };
  }
);

const ArrowUp = styled.div({ // arrow facing up
  position: 'absolute',
  width: 0,
  height: 0,
  zIndex: 10
}, ({ theme, iconWidth, fixedPosition, fixedAtTopLevel, dark, light }) => {
  const arrowThickness = convertToNumberFrom(theme.Tooltip.Arrow.thickness);
  const gutterAroundIcon = convertToNumberFrom(theme.Tooltip.gutterAroundIcon);

  return { // position the arrow for tip box specified as 'bottom left', 'bottom' or 'bottom right'
    ...!fixedAtTopLevel || !fixedPosition ? {
      top: (iconWidth || theme.Tooltip.Icon.width) + arrowThickness + gutterAroundIcon,
      left: (iconWidth || theme.Tooltip.Icon.width) / 2 - arrowThickness,
    } : { // When fixedPosition is available
      position: 'fixed',
      left: fixedPosition.x,
      top: fixedPosition.y
    },
    borderLeft: `${theme.Tooltip.Arrow.thickness}px solid transparent`,
    borderRight: `${theme.Tooltip.Arrow.thickness}px solid transparent`,
    borderBottom: `${theme.Tooltip.Arrow.thickness}px solid ${theme.Tooltip.Arrow.color[
      dark && 'dark' || light && 'light'
    ]}`,
  };
});

const ArrowDown = styled.div({ // arrow facing down
  position: 'absolute',
  width: 0,
  height: 0,
  zIndex: 10
}, ({ theme, iconWidth, fixedPosition, fixedAtTopLevel, dark, light }) => {
  const arrowThickness = convertToNumberFrom(theme.Tooltip.Arrow.thickness);
  const gutterAroundIcon = convertToNumberFrom(theme.Tooltip.gutterAroundIcon);

  return { // position the arrow for tip box specified as 'top left', 'top' or 'top right'
    ...!fixedAtTopLevel || !fixedPosition ? {
      top: -2 * arrowThickness - gutterAroundIcon,
      left: (iconWidth || theme.Tooltip.Icon.width) / 2 - arrowThickness,
    } : { // When fixedPosition is available
      position: 'fixed',
      left: fixedPosition.x,
      top: fixedPosition.y
    },
    borderLeft: `${theme.Tooltip.Arrow.thickness}px solid transparent`,
    borderRight: `${theme.Tooltip.Arrow.thickness}px solid transparent`,
    borderTop: `${theme.Tooltip.Arrow.thickness}px solid ${theme.Tooltip.Arrow.color[
      dark && 'dark' || light && 'light'
    ]}`,
  };
});

const ArrowLeft = styled.div({ // arrow facing left
  position: 'absolute',
  width: 0,
  height: 0,
  zIndex: 10
}, ({ theme, iconWidth, fixedPosition, fixedAtTopLevel, dark, light }) => { 
  const arrowThickness = convertToNumberFrom(theme.Tooltip.Arrow.thickness);
  const gutterAroundIcon = convertToNumberFrom(theme.Tooltip.gutterAroundIcon);

  return { // position the arrow for tip box specified as 'right'
    ...!fixedAtTopLevel || !fixedPosition ? {
      top: (iconWidth || theme.Tooltip.Icon.width) / 2 - arrowThickness, 
      left: (iconWidth || theme.Tooltip.Icon.width) + 2 * arrowThickness + gutterAroundIcon,
    } : { // When fixedPosition is available
      position: 'fixed',
      left: fixedPosition.x,
      top: fixedPosition.y
    },
    borderTop: `${theme.Tooltip.Arrow.thickness}px solid transparent`,
    borderBottom: `${theme.Tooltip.Arrow.thickness}px solid transparent`,
    borderRight: `${theme.Tooltip.Arrow.thickness}px solid ${theme.Tooltip.Arrow.color[
      dark && 'dark' || light && 'light'
    ]}`,
  };
});

const ArrowRight = styled.div({ // arrow facing right
  position: 'absolute',
  width: 0,
  height: 0,
  zIndex: 10
}, ({ theme, iconWidth, fixedPosition, fixedAtTopLevel, dark, light }) => {
  const arrowThickness = convertToNumberFrom(theme.Tooltip.Arrow.thickness);
  const gutterAroundIcon = convertToNumberFrom(theme.Tooltip.gutterAroundIcon);
  
  return { // position the arrow for tip box specified as 'left'
    ...!fixedAtTopLevel || !fixedPosition ? {
      top: (iconWidth || theme.Tooltip.Icon.width) / 2 - arrowThickness, 
      left: -2 * arrowThickness - gutterAroundIcon,
    } : { // When fixedPosition is available
      position: 'fixed',
      left: fixedPosition.x,
      top: fixedPosition.y
    },
    borderTop: `${theme.Tooltip.Arrow.thickness}px solid transparent`,
    borderBottom: `${theme.Tooltip.Arrow.thickness}px solid transparent`,
    borderLeft: `${theme.Tooltip.Arrow.thickness}px solid ${theme.Tooltip.Arrow.color[
      dark && 'dark' || light && 'light'
    ]}`,
  };
});

/**
 * Calculates how many positions are specified
 */
const getNumOfPositions = ({ 
  top, bottom, left, right, topLeft, topRight, bottomLeft, bottomRight 
}) => {
  const positions = [top, bottom, left, right, topLeft, topRight, bottomLeft, bottomRight];
  return positions.reduce((numOfPositions, current) => {
    if (current) return ++numOfPositions;
    return numOfPositions;
  }, 0);
};

/**
 * Converts string to integer, assuming `px` at the end
 */
export const convertToNumberFrom = value => {
  if (Number.isInteger(value)) return value; // return without conversion if already an integer
  return Number.parseInt(value.slice(0, value.length - 2)); // assuming 'px'
};

export const MMCTooltip = withTheme(TooltipComponent);
